import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { RangerAvatar, charImage, companionImage } from '../components/RangerAvatar';
import {
    Leaf, ArrowLeft, Star, Lock, Check, X, ShoppingBag, Sparkle
} from '@phosphor-icons/react';

const TABS = [
    { id: 'skin', label: 'Skin' },
    { id: 'outfit', label: 'Outfit' },
    { id: 'companion', label: 'Buddy' },
];

export default function CustomizeAvatarPage() {
    const { user, api, updateUser } = useAuth();
    const [catalog, setCatalog] = useState(null);
    const [sel, setSel] = useState({ skin: 'light', outfit: 'classic', companion: 'none' });
    const [tab, setTab] = useState('skin');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [buyTarget, setBuyTarget] = useState(null); // {category, item}
    const [buying, setBuying] = useState(false);
    const [celebrate, setCelebrate] = useState(false);

    useEffect(() => {
        fetchCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCatalog = async () => {
        try {
            const { data } = await api().get('/avatar/catalog');
            setCatalog(data);
            setSel(data.config || { skin: 'light', outfit: 'classic', companion: 'none' });
        } catch (e) {
            toast.error('Failed to load avatar shop');
        } finally {
            setLoading(false);
        }
    };

    const tokens = catalog?.tokens_balance ?? 0;
    const lifetimeXp = catalog?.lifetime_xp ?? 0;

    const thumbFor = (category, item) => {
        if (category === 'skin') return charImage({ outfit: sel.outfit, skin: item.id });
        if (category === 'outfit') return charImage({ outfit: item.id, skin: sel.skin });
        return companionImage(item.id);
    };

    const handleSelect = (category, item) => {
        if (!item.owned) {
            if (item.unlock_type === 'tokens') setBuyTarget({ category, item });
            return;
        }
        setSel((prev) => ({ ...prev, [category]: item.id }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api().put('/avatar/config', sel);
            updateUser({ avatar_config: sel });
            toast.success('Your Ranger looks great! 🎉');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save avatar');
        } finally {
            setSaving(false);
        }
    };

    const handleBuy = async () => {
        if (!buyTarget) return;
        setBuying(true);
        try {
            const { category, item } = buyTarget;
            const { data } = await api().post('/avatar/purchase', { category, item_id: item.id });
            // Mark owned + select + update balance
            setCatalog((prev) => {
                const key = category === 'outfit' ? 'outfits' : 'companions';
                return {
                    ...prev,
                    tokens_balance: data.tokens_balance,
                    [key]: prev[key].map((i) => (i.id === item.id ? { ...i, owned: true } : i)),
                };
            });
            setSel((prev) => ({ ...prev, [category]: item.id }));
            setBuyTarget(null);
            setCelebrate(true);
            setTimeout(() => setCelebrate(false), 3500);
            toast.success(`Unlocked ${item.name}! 🎉`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Purchase failed');
        } finally {
            setBuying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#fefce8] flex items-center justify-center">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading the Ranger Studio...</p>
                </div>
            </div>
        );
    }

    const items = catalog ? (tab === 'skin' ? catalog.skins : tab === 'outfit' ? catalog.outfits : catalog.companions) : [];

    return (
        <div className="min-h-screen bg-[#fefce8] dot-grid pb-28 md:pb-8">
            {celebrate && <Confetti recycle={false} numberOfPieces={250} />}

            {/* Nav */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/dashboard" className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-bold" data-testid="back-to-dashboard">
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <Leaf weight="fill" className="text-[#22c55e]" size={22} />
                            <span className="font-fredoka text-lg font-bold text-[#0f172a]">Ranger Studio</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-[#fde047] rounded-full border-4 border-[#0f172a]" data-testid="token-balance-display">
                            <Star weight="fill" className="text-[#0f172a]" size={18} />
                            <span className="font-fredoka font-bold text-[#0f172a]">{tokens}</span>
                        </div>
                    </div>
                </div>
            </nav>

            <main className="pt-20 px-4 max-w-7xl mx-auto md:grid md:grid-cols-12 md:gap-6">
                {/* Preview Panel */}
                <div className="md:col-span-5 lg:col-span-4 mb-6 md:mb-0">
                    <div className="md:sticky md:top-20">
                        <div className="bg-white border-4 border-[#0f172a] rounded-3xl shadow-[6px_6px_0px_#0f172a] overflow-hidden">
                            <div className="relative h-[42vh] md:h-[58vh] bg-gradient-to-b from-[#dcfce7] to-white">
                                <RangerAvatar config={sel} variant="full" className="p-4" data-testid="avatar-preview" />
                            </div>
                            <div className="border-t-4 border-[#0f172a] p-4">
                                <motion.button
                                    onClick={handleSave}
                                    disabled={saving}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="w-full bg-[#22c55e] text-white font-fredoka font-bold text-lg uppercase tracking-wide py-3 rounded-2xl border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] disabled:opacity-50"
                                    data-testid="save-avatar-button"
                                >
                                    {saving ? 'Saving...' : 'Save My Ranger'}
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Options Panel */}
                <div className="md:col-span-7 lg:col-span-8">
                    {/* Tabs */}
                    <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`flex-shrink-0 px-5 py-2 rounded-full border-4 border-[#0f172a] font-fredoka font-bold transition-all ${
                                    tab === t.id ? 'bg-[#22c55e] text-white shadow-[3px_3px_0px_#0f172a]' : 'bg-white text-[#0f172a]'
                                }`}
                                data-testid={`category-tab-${t.id}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4">
                        {items.map((item) => {
                            const isSelected = sel[tab] === item.id;
                            const locked = !item.owned;
                            const isNone = item.id === 'none';
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleSelect(tab, item)}
                                    disabled={locked && item.unlock_type === 'reward'}
                                    className={`relative aspect-square rounded-2xl border-4 overflow-hidden bg-white transition-all text-left ${
                                        isSelected ? 'border-[#22c55e] shadow-[4px_4px_0px_#22c55e] -translate-y-0.5' : 'border-[#0f172a] shadow-[3px_3px_0px_#0f172a]'
                                    } ${locked ? 'opacity-95' : 'hover:-translate-y-0.5'}`}
                                    data-testid={`item-swatch-${tab}-${item.id}`}
                                >
                                    {isNone ? (
                                        <div className="w-full h-full flex flex-col items-center justify-center bg-[#f1f5f9]">
                                            <X weight="bold" size={28} className="text-[#94a3b8]" />
                                        </div>
                                    ) : (
                                        <img
                                            src={thumbFor(tab, item)}
                                            alt={item.name}
                                            className={`w-full h-full ${tab === 'companion' ? 'object-contain p-2' : 'object-cover object-top'}`}
                                            onError={(e) => { e.target.style.opacity = 0.2; }}
                                        />
                                    )}

                                    {/* Name label */}
                                    <span className="absolute top-1 left-1 right-1 text-[10px] font-bold text-[#0f172a] bg-white/85 rounded px-1 py-0.5 truncate text-center pointer-events-none">
                                        {item.name}
                                    </span>

                                    {/* Selected check */}
                                    {isSelected && (
                                        <span className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-[#22c55e] border-2 border-white flex items-center justify-center">
                                            <Check weight="bold" size={14} className="text-white" />
                                        </span>
                                    )}

                                    {/* Locked overlays */}
                                    {locked && item.unlock_type === 'tokens' && (
                                        <span className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 bg-[#fde047] border-2 border-[#0f172a] rounded-full py-0.5" data-testid={`buy-badge-${item.id}`}>
                                            <Star weight="fill" size={12} className="text-[#0f172a]" />
                                            <span className="text-[11px] font-bold text-[#0f172a]">{item.cost}</span>
                                        </span>
                                    )}
                                    {locked && item.unlock_type === 'reward' && (
                                        <div className="absolute inset-0 bg-[#0f172a]/55 flex flex-col items-center justify-center p-1 text-center">
                                            <Lock weight="fill" size={20} className="text-white mb-1" />
                                            <span className="text-[9px] font-bold text-white leading-tight">{item.reward_label}</span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {tab === 'companion' && (
                        <p className="text-xs text-[#475569] mt-3 font-semibold">Tip: Buddies appear next to your Ranger across the app!</p>
                    )}
                </div>
            </main>

            {/* Buy modal */}
            <AnimatePresence>
                {buyTarget && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white border-4 border-[#0f172a] rounded-3xl shadow-[8px_8px_0px_#0f172a] p-6 w-full max-w-sm text-center"
                        >
                            <div className="flex justify-end">
                                <button onClick={() => setBuyTarget(null)} className="p-1" data-testid="buy-modal-close"><X weight="bold" size={22} /></button>
                            </div>
                            <div className="w-32 h-32 mx-auto mb-3 rounded-2xl border-4 border-[#0f172a] overflow-hidden bg-[#f1f5f9]">
                                <img
                                    src={thumbFor(buyTarget.category, buyTarget.item)}
                                    alt={buyTarget.item.name}
                                    className={`w-full h-full ${buyTarget.category === 'companion' ? 'object-contain p-2' : 'object-cover object-top'}`}
                                />
                            </div>
                            <h3 className="font-fredoka text-2xl font-bold text-[#0f172a] mb-1">{buyTarget.item.name}</h3>
                            <div className="flex items-center justify-center gap-1 mb-4">
                                <Star weight="fill" className="text-[#eab308]" size={20} />
                                <span className="font-fredoka font-bold text-[#0f172a]">{buyTarget.item.cost} Bush Tokens</span>
                            </div>
                            {tokens < buyTarget.item.cost ? (
                                <p className="text-[#fb7185] font-bold text-sm mb-4">You need {buyTarget.item.cost - tokens} more tokens. Complete missions to earn more!</p>
                            ) : (
                                <p className="text-[#475569] text-sm mb-4">You'll have {tokens - buyTarget.item.cost} tokens left.</p>
                            )}
                            <motion.button
                                onClick={handleBuy}
                                disabled={buying || tokens < buyTarget.item.cost}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="w-full bg-[#eab308] text-[#0f172a] font-fredoka font-bold text-lg uppercase py-3 rounded-2xl border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] disabled:opacity-50 flex items-center justify-center gap-2"
                                data-testid="buy-token-button"
                            >
                                <ShoppingBag weight="bold" size={20} />
                                {buying ? 'Unlocking...' : 'Buy with Tokens'}
                            </motion.button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
